import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  ArrowLeft,
  Archive,
  FileText,
  Receipt,
  Trash2,
  Plus,
  Copy,
  Pencil,
  History,
  AlertTriangle,
  Calendar,
  ExternalLink,
  Upload,
  Check,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Link2,
} from 'lucide-react';
import { useApp } from '@/store';
import { useT } from '@/lib/i18n';
import { fmtDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusBadge } from '@/components/StatusBadge';
import { AddressDialog } from '@/components/dialogs/AddressDialog';
import { ConditionValue, type Address, type Extinguisher } from '@/data/types';
import { geocode } from '@/lib/geocode';

type AddressKey =
  | 'billingAddress'
  | 'objectAddress'
  | 'ownerAddress'
  | 'contact';

type ExtSortKey =
  | 'floor'
  | 'location'
  | 'mfrModel'
  | 'type'
  | 'id'
  | 'year'
  | 'last'
  | 'next'
  | 'refilled'
  | 'billing'
  | 'status';

export function CustomerDetail() {
  const { id, tab } = useParams();
  const nav = useNavigate();
  const customer = useApp(s => s.customers.find(c => c.id === id));
  const allCustomers = useApp(s => s.customers);
  const toast = useApp(s => s.toast);
  const deleteCustomer = useApp(s => s.deleteCustomer);
  const updateCustomer = useApp(s => s.updateCustomer);
  const createArchive = useApp(s => s.createArchive);
  const deleteExtinguisher = useApp(s => s.deleteExtinguisher);
  const batchUpdateInspection = useApp(s => s.batchUpdateInspection);
  const deleteHose = useApp(s => s.deleteHose);
  const upsertExtinguisher = useApp(s => s.upsertExtinguisher);
  const types = useApp(s => s.deviceTypes);

  const t = useT();
  const [editing, setEditing] = useState<AddressKey | null>(null);
  const contractInputRef = useRef<HTMLInputElement>(null);
  const [sortKey, setSortKey] = useState<ExtSortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [linkQuery, setLinkQuery] = useState('');

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t('customer.notFound')}</p>
        <Button variant="link" onClick={() => nav('/overview')}>
          {t('common.backToOverview')}
        </Button>
      </div>
    );
  }

  const handleDelete = () => {
    if (confirm(`${t('customer.deleteConfirm')} ${customer.name}?`)) {
      deleteCustomer(customer.id);
      toast(`${t('customer.deleted')} ${customer.name}`);
      nav('/overview');
    }
  };

  const handleArchive = () => {
    createArchive(customer.id);
    toast(t('customer.archiveCreated'));
  };

  const handleBatchInspect = () => {
    batchUpdateInspection(customer.id);
    toast(t('customer.batchInspectDone'));
  };

  const saveAddress = (key: AddressKey) => async (a: Address) => {
    const patch: Partial<typeof customer> = { [key]: a };
    if (key === 'objectAddress') {
      const g = await geocode(a);
      if (g) {
        patch.lat = g.lat;
        patch.lng = g.lng;
        toast(t('customer.coordsResolved'));
      }
    }
    updateCustomer(customer.id, patch);
    toast(t('customer.addressSaved'));
  };

  const copyExtinguisher = (e: Extinguisher) => {
    const copy: Extinguisher = {
      ...e,
      id: `E-${Date.now()}`,
      deviceNumber: '',
    };
    upsertExtinguisher(customer.id, copy);
    toast(t('customer.copied'));
  };

  const removeExtinguisher = (e: Extinguisher) => {
    if (!confirm(`Delete extinguisher ${e.deviceNumber ?? e.id}?`)) return;
    deleteExtinguisher(customer.id, e.id);
    toast(t('customer.deleted'));
  };

  const ageOf = (e: Extinguisher) => {
    if (!e.year) return null;
    const dt = types.find(x => x.id === e.type);
    if (!dt) return null;
    const age = new Date().getFullYear() - e.year;
    if (age > dt.ageLimit)
      return { age, limit: dt.ageLimit, cls: dt.pressureClass };
    return null;
  };

  const deviceCounts = Object.entries(customer.deviceCounts ?? {});

  const isMaintainedThisCycle = (e: Extinguisher) => {
    if (e.maintainedThisCycle !== undefined) return e.maintainedThisCycle;
    if (!e.lastInspection || !customer.lastInspection) return false;
    return e.lastInspection >= customer.lastInspection;
  };

  const toggleMaintained = (e: Extinguisher) => {
    upsertExtinguisher(customer.id, {
      ...e,
      maintainedThisCycle: !isMaintainedThisCycle(e),
    });
  };

  const toggleSort = (key: ExtSortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortValue = (e: Extinguisher, key: ExtSortKey): string | number => {
    switch (key) {
      case 'floor':
        return e.floor ?? '';
      case 'location':
        return e.location ?? '';
      case 'mfrModel':
        return `${e.mfr ?? ''} ${e.model ?? ''}`;
      case 'type':
        return e.type ?? '';
      case 'id':
        return e.deviceNumber ?? '';
      case 'year':
        return e.year ?? 0;
      case 'last':
        return e.lastInspection ?? '';
      case 'next':
        return e.nextInspection ?? '';
      case 'refilled':
        return e.refilled ?? '';
      case 'billing':
        return e.billing ?? '';
      case 'status':
        return isMaintainedThisCycle(e) ? 1 : 0;
    }
  };

  const SortHeader = ({
    sortKeyProp,
    children,
    className,
  }: {
    sortKeyProp: ExtSortKey;
    children: React.ReactNode;
    className?: string;
  }) => {
    const active = sortKey === sortKeyProp;
    const Icon = !active
      ? ArrowUpDown
      : sortDir === 'asc'
        ? ArrowUp
        : ArrowDown;
    return (
      <TableHead className={className}>
        <button
          type="button"
          onClick={() => toggleSort(sortKeyProp)}
          className={`inline-flex items-center gap-1 hover:text-foreground ${
            active ? 'text-primary font-semibold' : ''
          }`}
        >
          {children}
          <Icon className="h-3 w-3" />
        </button>
      </TableHead>
    );
  };

  const linkedCustomers = (customer.linkedCustomerIds ?? [])
    .map(lid => allCustomers.find(c => c.id === lid))
    .filter((c): c is NonNullable<typeof c> => Boolean(c));

  const linkCandidates = allCustomers
    .filter(c => c.id !== customer.id)
    .filter(c => !(customer.linkedCustomerIds ?? []).includes(c.id))
    .filter(c =>
      linkQuery
        ? `${c.name} ${c.id}`.toLowerCase().includes(linkQuery.toLowerCase())
        : true
    )
    .slice(0, 20);

  const addLinkedCustomer = (otherId: string) => {
    const myIds = customer.linkedCustomerIds ?? [];
    if (myIds.includes(otherId)) return;
    updateCustomer(customer.id, {
      linkedCustomerIds: [...myIds, otherId],
    });
    const other = allCustomers.find(c => c.id === otherId);
    if (other) {
      const otherIds = other.linkedCustomerIds ?? [];
      if (!otherIds.includes(customer.id)) {
        updateCustomer(otherId, {
          linkedCustomerIds: [...otherIds, customer.id],
        });
      }
    }
    setLinkQuery('');
    setLinkPickerOpen(false);
  };

  const removeLinkedCustomer = (otherId: string) => {
    updateCustomer(customer.id, {
      linkedCustomerIds: (customer.linkedCustomerIds ?? []).filter(
        x => x !== otherId
      ),
    });
    const other = allCustomers.find(c => c.id === otherId);
    if (other) {
      updateCustomer(otherId, {
        linkedCustomerIds: (other.linkedCustomerIds ?? []).filter(
          x => x !== customer.id
        ),
      });
    }
  };

  const addressCards: { title: string; addr?: Address; key: AddressKey }[] = [
    {
      title: t('customer.billingAddress'),
      addr: customer.billingAddress,
      key: 'billingAddress',
    },
    {
      title: t('customer.objectAddress'),
      addr: customer.objectAddress,
      key: 'objectAddress',
    },
    {
      title: t('customer.ownerAddress'),
      addr: customer.ownerAddress,
      key: 'ownerAddress',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
      data-tour="customer-detail"
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link
            to="/overview"
            className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-3 w-3" /> {t('common.backToOverview')}
          </Link>
          <div className="flex items-center gap-3 mt-2">
            <h1 className="text-2xl font-bold tracking-tight">
              {customer.name}
            </h1>
            <StatusBadge status={customer.status} />
            <Badge variant="outline" className="font-mono">
              {customer.id}
            </Badge>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={handleArchive}>
            <Archive className="h-4 w-4" />
            {t('customer.archive')}
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link
              to={`/customer/${customer.id}/invoice`}
              data-tour="invoice-btn"
            >
              <Receipt className="h-4 w-4" />
              {t('invoice.title')}
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/customer/${customer.id}/hose-report`}>
              <FileText className="h-4 w-4" />
              {t('hoseReport.title')}
            </Link>
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
            {t('common.delete')}
          </Button>
        </div>
      </div>

      <Tabs
        value={tab ?? 'info'}
        onValueChange={v => nav(`/customer/${customer.id}/${v}`)}
      >
        <TabsList className="w-full overflow-x-auto justify-start sm:justify-center">
          <TabsTrigger value="info">{t('customer.info')}</TabsTrigger>
          <TabsTrigger value="extinguishers">
            {t('customer.extinguishers')}
          </TabsTrigger>
          <TabsTrigger value="hoses">{t('customer.hoses')}</TabsTrigger>
          <TabsTrigger value="history">{t('customer.history')}</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
            {addressCards.map(x => (
              <Card key={x.title}>
                <CardHeader>
                  <CardTitle className="text-sm">{x.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                  {x.addr ? (
                    <>
                      {x.addr.company && (
                        <div className="font-medium">{x.addr.company}</div>
                      )}
                      {x.addr.attn && (
                        <div className="text-muted-foreground">
                          {t('address.attn')} {x.addr.attn}
                        </div>
                      )}
                      {x.addr.street && (
                        <div className="text-muted-foreground">
                          {x.addr.street}
                        </div>
                      )}
                      {(x.addr.zip || x.addr.city) && (
                        <div className="text-muted-foreground">
                          {x.addr.zip} {x.addr.city}
                        </div>
                      )}
                      {x.addr.phone && (
                        <div className="text-muted-foreground">
                          {t('customer.tel')} {x.addr.phone}
                        </div>
                      )}
                      {x.addr.email && (
                        <div className="text-muted-foreground">
                          {x.addr.email}
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2"
                        onClick={() => setEditing(x.key)}
                      >
                        <Pencil className="h-3 w-3" />
                        {t('common.edit')}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(x.key)}
                    >
                      <Plus className="h-3 w-3" />
                      {t('common.add')}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">
                    {t('customer.contact')}
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing('contact')}
                  >
                    <Pencil className="h-3 w-3" />
                    {t('common.edit')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                {customer.contact ? (
                  <>
                    <div className="font-medium">{customer.contact.attn}</div>
                    <div className="text-muted-foreground">
                      {customer.contact.phone}
                    </div>
                    {customer.contact.email && (
                      <div className="text-muted-foreground">
                        {customer.contact.email}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-muted-foreground">
                    {t('common.notSet')}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card data-focus="customer-dates">
              <CardHeader>
                <CardTitle className="text-sm">
                  {t('customer.metadata')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('customer.created')}
                  </span>
                  <span>{fmtDate(customer.created)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('customer.createdBy')}
                  </span>
                  <span>{customer.createdBy ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('customer.worker')}
                  </span>
                  <span>{customer.worker ?? '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('customer.lastInspection')}
                  </span>
                  <span>{fmtDate(customer.lastInspection)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('customer.nextInspection')}
                  </span>
                  <span>{fmtDate(customer.nextInspection)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('customer.coordinates')}
                  </span>
                  <span className="font-mono text-xs">
                    {customer.lat?.toFixed(5) ?? '—'},{' '}
                    {customer.lng?.toFixed(5) ?? '—'}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card data-focus="service-contract">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">
                    {t('customer.serviceContract')}
                  </CardTitle>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-sm">
                      {customer.serviceContract
                        ? t('common.yes')
                        : t('common.no')}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={customer.serviceContract ?? false}
                      onClick={() =>
                        updateCustomer(customer.id, {
                          serviceContract: !(customer.serviceContract ?? false),
                        })
                      }
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                        customer.serviceContract ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow-lg ring-0 transition-transform ${
                          customer.serviceContract
                            ? 'translate-x-5'
                            : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </label>
                </div>
              </CardHeader>
              {customer.serviceContract && (
                <CardContent className="space-y-2">
                  <input
                    ref={contractInputRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        updateCustomer(customer.id, {
                          serviceContractPdf: reader.result as string,
                        });
                        toast(t('customer.contractUploaded'));
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => contractInputRef.current?.click()}
                    >
                      <Upload className="h-3 w-3" />
                      {t('customer.uploadContract')}
                    </Button>
                    {customer.serviceContractPdf && (
                      <Button variant="outline" size="sm" asChild>
                        <a
                          href={customer.serviceContractPdf}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {t('customer.viewContract')}
                        </a>
                      </Button>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>

            <Card data-focus="linked-customers">
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle className="text-sm">
                    {t('customer.linkedCustomers')}
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setLinkPickerOpen(v => !v)}
                  >
                    <Link2 className="h-3 w-3" />
                    {t('customer.linkCustomer')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {linkPickerOpen && (
                  <div className="space-y-1 border rounded-md p-2 bg-muted/30">
                    <input
                      type="text"
                      placeholder={t('customer.searchCustomers')}
                      value={linkQuery}
                      onChange={e => setLinkQuery(e.target.value)}
                      className="w-full px-2 py-1 text-sm rounded border bg-background"
                      autoFocus
                    />
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {linkCandidates.length === 0 ? (
                        <div className="text-xs text-muted-foreground px-2 py-1">
                          {t('customer.noLinkedCustomers')}
                        </div>
                      ) : (
                        linkCandidates.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => addLinkedCustomer(c.id)}
                            className="w-full text-left text-sm px-2 py-1 rounded hover:bg-accent flex items-center justify-between gap-2"
                          >
                            <span className="truncate">{c.name}</span>
                            <span className="font-mono text-xs text-muted-foreground">
                              {c.id}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
                {linkedCustomers.length === 0 ? (
                  <div className="text-xs text-muted-foreground">
                    {t('customer.noLinkedCustomers')}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {linkedCustomers.map(lc => (
                      <Badge
                        key={lc.id}
                        variant="secondary"
                        className="gap-1 pl-2 pr-1 py-1"
                      >
                        <Link
                          to={`/customer/${lc.id}`}
                          className="hover:underline"
                        >
                          {lc.name}
                        </Link>
                        <button
                          type="button"
                          onClick={() => removeLinkedCustomer(lc.id)}
                          className="ml-1 rounded hover:bg-destructive/20 p-0.5"
                          aria-label="Remove"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('customer.notes')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                defaultValue={customer.notes ?? ''}
                placeholder={t('customer.addNotes')}
                onBlur={e =>
                  updateCustomer(customer.id, { notes: e.target.value })
                }
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">
                {t('customer.equipmentSummary')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {deviceCounts.length === 0 && (
                  <span className="text-sm text-muted-foreground">
                    {t('customer.noEquipment')}
                  </span>
                )}
                {deviceCounts.map(([k, v]) => (
                  <Badge key={k} variant={v > 0 ? 'default' : 'secondary'}>
                    {k}: {v}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="extinguishers" className="space-y-3">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-semibold">
                {t('customer.extinguishers')} ({customer.extinguishers.length})
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('customer.sortedByFloor')}
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleBatchInspect}>
                <Calendar className="h-4 w-4" />
                {t('customer.batchInspect')}
              </Button>
              <Button size="sm" asChild data-tour="add-extinguisher">
                <Link to={`/customer/${customer.id}/extinguisher/new`}>
                  <Plus className="h-4 w-4" />
                  {t('common.add')}
                </Link>
              </Button>
            </div>
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHeader
                    sortKeyProp="floor"
                    className="hidden md:table-cell"
                  >
                    {t('table.floor')}
                  </SortHeader>
                  <SortHeader sortKeyProp="location">
                    {t('table.location')}
                  </SortHeader>
                  <SortHeader sortKeyProp="mfrModel">
                    {t('table.mfrModel')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="type"
                    className="hidden sm:table-cell"
                  >
                    {t('table.type')}
                  </SortHeader>
                  <SortHeader sortKeyProp="id" className="hidden lg:table-cell">
                    {t('table.id')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="year"
                    className="hidden sm:table-cell"
                  >
                    {t('table.year')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="last"
                    className="hidden md:table-cell"
                  >
                    {t('table.last')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="next"
                    className="hidden md:table-cell"
                  >
                    {t('table.next')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="refilled"
                    className="hidden lg:table-cell"
                  >
                    {t('table.refilled')}
                  </SortHeader>
                  <SortHeader
                    sortKeyProp="billing"
                    className="hidden lg:table-cell"
                  >
                    {t('table.billing')}
                  </SortHeader>
                  <TableHead>{t('table.actions')}</TableHead>
                  <SortHeader sortKeyProp="status">
                    {t('table.status')}
                  </SortHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const renderRow = (e: Extinguisher) => {
                    const warn = ageOf(e);
                    const maintained = isMaintainedThisCycle(e);
                    return (
                      <TableRow key={e.id}>
                        <TableCell className="hidden md:table-cell">
                          {e.floor}
                        </TableCell>
                        <TableCell>{e.location}</TableCell>
                        <TableCell className="text-sm">
                          {e.mfr} {e.model}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Badge
                            variant="outline"
                            className="font-mono text-[10px]"
                          >
                            {e.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs hidden lg:table-cell">
                          {e.deviceNumber}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <span
                            className={`flex items-center gap-1 ${warn ? 'text-amber-600 dark:text-amber-400 font-semibold' : ''}`}
                            title={
                              warn
                                ? `${warn.age}y exceeds ${warn.limit}y ${warn.cls} limit`
                                : undefined
                            }
                          >
                            {e.year}
                            {warn && <AlertTriangle className="h-3 w-3" />}
                          </span>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {fmtDate(e.lastInspection)}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {fmtDate(e.nextInspection)}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-muted-foreground">
                          {fmtDate(e.refilled)}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <Badge variant="secondary">{e.billing}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" asChild>
                              <Link
                                to={`/customer/${customer.id}/extinguisher/${e.id}`}
                              >
                                <Pencil className="h-3 w-3" />
                              </Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => copyExtinguisher(e)}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeExtinguisher(e)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <button
                            type="button"
                            onClick={() => toggleMaintained(e)}
                            title={
                              maintained
                                ? t('ext.maintainedThisCycle')
                                : t('ext.notMaintained')
                            }
                            className="rounded p-0.5 hover:bg-muted transition-colors"
                          >
                            {maintained ? (
                              <Check
                                className="h-4 w-4 text-emerald-600 dark:text-emerald-400"
                                aria-label={t('ext.maintainedThisCycle')}
                              />
                            ) : (
                              <X
                                className="h-4 w-4 text-orange-500"
                                aria-label={t('ext.notMaintained')}
                              />
                            )}
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  };

                  if (sortKey) {
                    const sorted = [...customer.extinguishers].sort((a, b) => {
                      const va = sortValue(a, sortKey);
                      const vb = sortValue(b, sortKey);
                      let cmp = 0;
                      if (typeof va === 'number' && typeof vb === 'number') {
                        cmp = va - vb;
                      } else {
                        cmp = String(va).localeCompare(String(vb));
                      }
                      return sortDir === 'asc' ? cmp : -cmp;
                    });
                    return sorted.map(renderRow);
                  }

                  const grouped: Record<string, Extinguisher[]> = {};
                  customer.extinguishers.forEach(e => {
                    const sec = e.section || '—';
                    (grouped[sec] ??= []).push(e);
                  });
                  const sections = Object.entries(grouped);
                  const showSections =
                    sections.length > 1 ||
                    (sections.length === 1 && sections[0][0] !== '—');
                  return sections.flatMap(([sec, exts]) => {
                    const rows = exts.map(renderRow);
                    if (!showSections) return rows;
                    return [
                      <TableRow key={`sec-${sec}`} className="bg-primary/5">
                        <TableCell
                          colSpan={99}
                          className="py-1 px-3 font-semibold text-xs text-primary"
                        >
                          {sec} ({exts.length})
                        </TableCell>
                      </TableRow>,
                      ...rows,
                    ];
                  });
                })()}
                {customer.extinguishers.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={99}
                      className="text-center text-muted-foreground py-8"
                    >
                      {t('customer.noExtinguishers')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="hoses" className="space-y-3">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-semibold">
                {t('customer.hoses')} ({customer.hoses.length})
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('hose.hoseStandard')}
              </p>
            </div>
            <Button size="sm" asChild>
              <Link to={`/customer/${customer.id}/hose/new`}>
                <Plus className="h-4 w-4" />
                {t('hose.addHose')}
              </Link>
            </Button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {customer.hoses.map(h => (
              <Card key={h.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">
                      {h.mfr} {h.model}{' '}
                      <span className="text-muted-foreground font-mono text-xs ml-1">
                        {h.id}
                      </span>
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={h.status} />
                      <Button
                        variant="ghost"
                        size="icon"
                        asChild
                        className="h-11 w-11 sm:h-7 sm:w-7"
                      >
                        <Link
                          to={`/customer/${customer.id}/hose/${h.id}`}
                          aria-label="Edit hose"
                        >
                          <Pencil className="h-3 w-3" />
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 sm:h-7 sm:w-7"
                        onClick={() => {
                          if (confirm(`Delete hose ${h.id}?`)) {
                            deleteHose(customer.id, h.id);
                            toast('Hose deleted');
                          }
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{h.location}</p>
                </CardHeader>
                <CardContent className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 text-xs">
                  <div>
                    <div className="font-semibold mb-1">
                      {t('hose.equipment')}
                    </div>
                    <div>
                      {t('table.year')}: {h.year}
                    </div>
                    <div>
                      {t('hose.length')}: {h.length} m
                    </div>
                    <div>Ø: {h.diameter}</div>
                    <div>Conn: {h.connection}</div>
                  </div>
                  <div>
                    <div className="font-semibold mb-1">
                      {t('hose.condition')}
                    </div>
                    {Object.entries(h.condition)
                      .slice(0, 4)
                      .map(([k, v]) => (
                        <div key={k} className="flex justify-between">
                          <span>{k}</span>
                          <Badge
                            variant={
                              v === ConditionValue.OK
                                ? 'success'
                                : v === ConditionValue.NOT_OK
                                  ? 'destructive'
                                  : 'info'
                            }
                            className="text-[10px]"
                          >
                            {v}
                          </Badge>
                        </div>
                      ))}
                  </div>
                  <div>
                    <div className="font-semibold mb-1">
                      {t('hose.service')}
                    </div>
                    <div>
                      {t('table.last')}: {h.lastService}
                    </div>
                    <div>
                      {t('table.next')}: {h.nextService}
                    </div>
                    <div>Flow: {h.flowRate}</div>
                    <div>12-bar: {h.pressureTestDate}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {customer.hoses.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t('customer.noHoses')}
              </p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-3">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-semibold flex items-center gap-2">
                <History className="h-4 w-4" />
                {t('customer.history')}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('customer.readOnlySnapshots')}
              </p>
            </div>
            <Button size="sm" onClick={handleArchive}>
              <Plus className="h-4 w-4" />
              {t('customer.createArchive')}
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            {customer.archives.map(a => (
              <Card key={a.id}>
                <CardContent className="pt-5 space-y-1 text-sm">
                  <div className="font-semibold">{a.date}</div>
                  <div className="text-muted-foreground text-xs">
                    {a.deviceCount} {t('customer.devices')} — {a.technician}
                  </div>
                  <Badge variant="outline" className="text-[10px] mt-1">
                    {a.id}
                  </Badge>
                </CardContent>
              </Card>
            ))}
            {customer.archives.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t('customer.noArchives')}
              </p>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {editing && (
        <AddressDialog
          open={true}
          onOpenChange={v => !v && setEditing(null)}
          title={
            editing === 'billingAddress'
              ? t('customer.billingAddress')
              : editing === 'objectAddress'
                ? t('customer.objectAddress')
                : editing === 'ownerAddress'
                  ? t('customer.ownerAddress')
                  : t('customer.contact')
          }
          initial={customer[editing] as Address | undefined}
          onSave={saveAddress(editing)}
          onGeocode={
            editing === 'objectAddress'
              ? async a => {
                  const g = await geocode(a);
                  return g;
                }
              : undefined
          }
        />
      )}
    </motion.div>
  );
}
